import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useStateValue } from '@State'
import { saveAnswer } from '../../api'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { SaveIndicator } from './utils/SaveIndicator'
import { ScrollShadow } from './utils/ScrollShadow'
import {
  FEEDBACK_COLORS, FEEDBACK_LABELS, getOptionFeedbackStatus,
  FEEDBACK_BADGE,
} from './utils/feedbackStyles'
import { stripHtml } from './utils/stripHtml'

const CELL_FEEDBACK_BG = {
  correct: { backgroundColor: FEEDBACK_COLORS.correct.bg },
  incorrect: { backgroundColor: FEEDBACK_COLORS.incorrect.bg },
  missed: { backgroundColor: FEEDBACK_COLORS.missed.bg },
  neutral: { backgroundColor: '#f0f0f0', opacity: 0.5 },
}

const INPUT_STYLE = { cursor: 'pointer', width: '18px', height: '18px' }
const INPUT_DISABLED = { ...INPUT_STYLE, cursor: 'default', opacity: 0.6 }

/**
 * Parse saved answer JSON into a selections map: { "rowGuid-colNum": boolean }
 */
function parseAnswer(answer) {
  if (!answer) return {}
  try {
    return JSON.parse(answer)
  } catch {
    return {}
  }
}

export const NGNMatrixQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly } = state
  const isSaving = savingGuids.has(question.guid)

  const [selections, setSelections] = useState(() => parseAnswer(question.answer))

  const inputType = question.data.inputType || 'radio'

  useEffect(() => {
    const parsed = JSON.parse(question.answer || '{}')
    let value
    if (inputType === 'checkbox') {
      // matrix-multiple-select: each column must have at least 1 selection.
      // Answer keys are "${rowGuid}-${colNum}" (colNum is 1-indexed).
      const numCols = (question.data.tableColumns || []).length
      const selectedCols = new Set(
        Object.entries(parsed)
          .filter(([, v]) => v)
          .map(([k]) => k.split('-').pop())
      )
      value = selectedCols.size >= numCols
    } else {
      const { minSelections, maxSelections } = question.data
      const selected = Object.values(parsed).filter(Boolean).length
      value = selected >= minSelections && selected <= maxSelections
    }
    dispatch({ type: 'SET_FULLY_ANSWERED', guid: question.guid, value })
  }, [question.answer, inputType])

  const save = useCallback(async (newSelections) => {
    const answerJson = JSON.stringify(newSelections)
    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: answerJson })
    dispatch({ type: 'SAVE_START', guid: question.guid })
    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, answerJson)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }, [coursenum, activitySubmissionId, question.guid, dispatch])

  const handleChange = useCallback((rowGuid, colNum) => {
    if (readonly || isSaving) return

    setSelections(prev => {
      const next = { ...prev }

      if (inputType === 'radio') {
        // Deselect all columns for this row, then select the clicked one
        for (const key of Object.keys(next)) {
          if (key.startsWith(rowGuid + '-')) {
            next[key] = false
          }
        }
        next[`${rowGuid}-${colNum}`] = true
      } else {
        // Toggle checkbox
        const key = `${rowGuid}-${colNum}`
        next[key] = !next[key]
      }

      save(next)
      return next
    })
  }, [readonly, isSaving, inputType, save])

  const { showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const correctByRow = useMemo(() => {
    if (!showFeedback || !question.correctAnswer) return null
    return question.correctAnswer
  }, [showFeedback, question.correctAnswer])
  const showCellFeedback = showFeedback && showAnswer && correctByRow

  return (
    <>
      <FeedbackDisplay question={question} />
      <div className="position-relative">
        <SaveIndicator questionGuid={question.guid} />
      </div>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
      <div
        role="heading"
        aria-level={3}
        className="mb-2 text-left"
        dangerouslySetInnerHTML={{ __html: question.text }}
      />

      <ScrollShadow>
        <table className="table table-bordered" role="grid">
          <thead>
            <tr>
              <th scope="col" className="bg-light font-weight-bold">{question.data.matrixTitle}</th>
              {question.data.tableColumns.map(col => (
                <th key={col.guid} scope="col" className="bg-light font-weight-bold text-center">{col.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {question.data.tableRows.map(row => (
              <tr key={row.guid}>
                <th scope="row" className="text-left font-weight-normal align-middle" dangerouslySetInnerHTML={{ __html: row.text || '' }} />
                {question.data.tableColumns.map((col, colIdx) => {
                  const colNum = colIdx + 1
                  const key = `${row.guid}-${colNum}`
                  const checked = !!selections[key]
                  const isCorrectCell = correctByRow && correctByRow[row.guid] && correctByRow[row.guid].includes(colNum)

                  let cellStyle = undefined
                  let badgeEl = null
                  if (showCellFeedback) {
                    const status = getOptionFeedbackStatus(checked, isCorrectCell)
                    cellStyle = CELL_FEEDBACK_BG[status]
                    if (status !== 'neutral') {
                      badgeEl = (
                        <div>
                          <span style={FEEDBACK_BADGE[status]}>{FEEDBACK_LABELS[status]}</span>
                        </div>
                      )
                    }
                  }

                  return (
                    <td key={col.guid} className="text-center align-middle" style={cellStyle}>
                      <input
                        type={inputType}
                        name={inputType === 'radio' ? row.guid : key}
                        checked={checked}
                        disabled={readonly || isSaving}
                        aria-label={`${stripHtml(row.text)} - ${stripHtml(col.title)}`}
                        onChange={() => handleChange(row.guid, colNum)}
                        style={readonly || isSaving ? INPUT_DISABLED : INPUT_STYLE}
                      />
                      {badgeEl}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollShadow>
      </TabSplitLayout>
    </>
  )
}
